import React, { FC } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, FormControlLabel, FormGroup, Switch, TextField } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { L } from 'harmony-language';
import { CreateOrganization, KeysWithValsOfType, Organization, OrganizationSetting } from '../../../../types';
import InputAdornment from '@mui/material/InputAdornment';
import { useFeatures } from '../../../user/selectors/use-permissions';
import { DriverReport, Features } from 'harmony-constants';

interface MobileSettingsProps {
    organization: CreateOrganization | Organization;
    setOrganization: (o: CreateOrganization | Organization) => void;
    etaThresholdMinutesError: boolean;
    insideSiteInactiveMinutesError: boolean;
    outsideSiteInactiveMinutesError: boolean;
}

export const MobileSettings: FC<MobileSettingsProps> = (props) => {
    const { organization, setOrganization, etaThresholdMinutesError, insideSiteInactiveMinutesError, outsideSiteInactiveMinutesError } = props;
    const hasMobileStopSplit = useFeatures(Features.MobileStopSplit);

    const handleSettingsBoolChange = (key: KeysWithValsOfType<OrganizationSetting, boolean>, value: boolean) => {
        const newOrganization = {
            ...organization,
            settings: {
                ...organization.settings,
                [key]: value,
            },
        }

        setOrganization(newOrganization);
    }

    return (
        <Accordion>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
            >
                {L.mobileSettings()}
            </AccordionSummary>
            <AccordionDetails>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
                    <TextField
                        variant='standard'
                        type='number'
                        label={L.ETAThresholdSettingLabel()}
                        fullWidth={true}
                        value={organization.etaThresholdMinutes === 0 ? '' : organization.etaThresholdMinutes}
                        error={etaThresholdMinutesError}
                        helperText={etaThresholdMinutesError && L.invalidThresholdNumber()}
                        InputProps={{
                            inputProps: { min: 0, max: 600 },
                            endAdornment: <InputAdornment position='end'>{L.mins()}</InputAdornment>
                        }}
                        onChange={(e) => {
                            setOrganization({ ...organization, etaThresholdMinutes: Number(e.target.value) });
                        }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
                        <TextField
                            variant='standard'
                            type='number'
                            label={L.unscheduledStopInsideSite()}
                            fullWidth={true}
                            value={organization.settings.insideSiteInactiveMinutes === 0 ? '' : organization.settings.insideSiteInactiveMinutes}
                            error={insideSiteInactiveMinutesError}
                            helperText={insideSiteInactiveMinutesError && L.invalidThresholdNumber()}
                            InputProps={{
                                inputProps: { min: 0, max: 600 },
                                endAdornment: <InputAdornment position='end'>{L.mins()}</InputAdornment>
                            }}
                            onChange={(e) => {
                                const newOrganization = {
                                    ...organization,
                                    settings: {
                                        ...organization.settings,
                                        insideSiteInactiveMinutes: Number(e.target.value),
                                    },
                                }

                                setOrganization(newOrganization);
                            }}
                        />
                        <TextField
                            variant='standard'
                            type='number'
                            label={L.unscheduledStopOutsideSite()}
                            fullWidth={true}
                            value={organization.settings.outsideSiteInactiveMinutes === 0 ? '' : organization.settings.outsideSiteInactiveMinutes}
                            error={outsideSiteInactiveMinutesError}
                            helperText={outsideSiteInactiveMinutesError && L.invalidThresholdNumber()}
                            InputProps={{
                                inputProps: { min: 0, max: 600 },
                                endAdornment: <InputAdornment position='end'>{L.mins()}</InputAdornment>
                            }}
                            onChange={(e) => {
                                const newOrganization = {
                                    ...organization,
                                    settings: {
                                        ...organization.settings,
                                        outsideSiteInactiveMinutes: Number(e.target.value),
                                    },
                                }

                                setOrganization(newOrganization);
                            }}
                        />
                    </div>
                    <FormGroup>
                        <FormControlLabel
                            control={<Switch
                                checked={organization.settings.driverAssignCompartments === DriverReport.yes}
                                onChange={(e) => {
                                    const newOrganization = {
                                        ...organization,
                                        settings: {
                                            ...organization.settings,
                                            driverAssignCompartments: e.target.checked ? DriverReport.yes : DriverReport.no,
                                        },
                                    }

                                    setOrganization(newOrganization);
                                }}
                            />}
                            label={L.driverCanAssignCompartments()}
                        />
                        {hasMobileStopSplit && <FormControlLabel
                            control={<Switch
                                checked={organization.settings.enforceSameCustomerSplitLocations}
                                onChange={(e) => {
                                    handleSettingsBoolChange('enforceSameCustomerSplitLocations', e.target.checked);
                                }}
                            />}
                            label={L.enforceSameCustomerSplitLocations()}
                        />}
                        <FormControlLabel
                            control={<Switch
                                checked={organization.settings.navigateOnStart}
                                onChange={(e) => {
                                    handleSettingsBoolChange('navigateOnStart', e.target.checked);
                                }}
                            />}
                            label={L.navigateOnStart()}
                        />
                        <FormControlLabel
                            control={<Switch
                                checked={organization.settings.upcoming}
                                onChange={(e) => {
                                    handleSettingsBoolChange('upcoming', e.target.checked);
                                }}
                            />}
                            label={L.viewUpcomingLoads()}
                        />
                    </FormGroup>
                </div>
            </AccordionDetails>
        </Accordion>
    );
};
